<template>
  <FullWindow :zIndex="100000" :bgcolor="color">
    <CloseButton :Method="Method" :xColor="typeColor"/>
    <ContentWrapper :typecolor="typeColor">
      <div class="about-type">
        <div class="inner-type" v-html="Data.about_statement"/>
      </div>
      <Links :typecolor="typeColor" :bgcolor="color">
        <li v-for="(item, i) in Data.external_links" v-bind:key="`${i}-nav`">
          <a class="link" :href="item.link_url" target="_blank">
            <span>{{item.link_title}}</span>
          </a>
        </li>
        <li>
          <ContactButton :href="`mailto:${Social.email}`" :typecolor="typeColor" :bgcolor="color">
            <span>Contact</span>
          </ContactButton>
        </li>
      </Links>
      <Socials :typecolor="typeColor" :bgcolor="color">
        <li>
          <FaceBook :color="typeColor" :link="Social.facebook"/>
        </li>
        <li>
          <Instagram :color="typeColor" :link="Social.instagram"/>
        </li>
        <li>
          <Soundcloud :color="typeColor" :link="Social.soundcloud"/>
        </li>
        <li>
          <Spotify :color="typeColor" :link="Social.spotify"/>
        </li>
        <li>
          <Twitter :color="typeColor" :link="Social.twitter"/>
        </li>
        <li>
          <Youtube :color="typeColor" :link="Social.youtube"/>
        </li>
        <li>
          <Twitch :color="typeColor" :link="'https://www.twitch.tv/futureclassic'"/>
        </li>
      </Socials>
      <PrivacyLink :typecolor="typeColor">
        <router-link :to="'/privacy'"><span>Privacy</span></router-link>
      </PrivacyLink>
    </ContentWrapper>
  </FullWindow>
</template>

<script>
import styled, { css } from 'vue-styled-components'
import Color from 'color'
import * as _ from '@/styles/mixins'
import { colors } from '@/styles/theme'
import FullWindow from '@/components/FullWindow'
import randomArrItem from '@/scripts/randomArrItem'
import CloseButton from '@/components/CloseButton'
import FaceBook from '@/components/socials/Facebook'
import Instagram from '@/components/socials/Instagram'
import Soundcloud from '@/components/socials/Soundcloud'
import Spotify from '@/components/socials/Spotify'
import Twitter from '@/components/socials/Twitter'
import Youtube from '@/components/socials/Youtube'
import Twitch from '@/components/socials/Twitch'

const props = {
  typecolor: String,
  bgcolor: String
}

const bigType = css`
  font-size: 2rem;
  padding-bottom: 1.5rem;
  ${_.media.desktop`
    font-size: 3.75vmin!important;
    padding-bottom: 2vmin!important;
  `}
  ${_.media.big`
    font-size: 4.25rem!important;
    padding-bottom: 2rem!important;
  `}
`

const PrivacyLink = styled('div', props)`
  ${_.smallType};
  position: relative;
  transform: translateY(1rem);
  padding: 1rem 2rem;
  text-transform: uppercase;
  text-align: right;
  ${_.media.desktop`
    position: absolute;
    bottom: 2rem;
    left: 2rem;
    padding: 1rem 0rem;
  `}
  a {
    ${_.linkInit};
    color: ${props => props.typecolor}!important;
    ${_.media.hover`
      &:hover {
        color: ${props => Color(props.typecolor).rotate(-90).saturate(0.25).hex()}!important;
      }
    `}
  }
`

const ContactButton = styled('a', props)`
  ${_.linkInit};
  ${bigType};
  color: ${props => props.typecolor}!important;
  background-color: ${props => props.bgcolor}!important;
  padding: .75rem 1.25rem;
  padding-bottom: 1.05rem!important;
  display: block;
  position: absolute;
  flex: 0;
  transform: rotate(1deg);
  z-index: 1000;
  transition: transform 150ms ease-in-out;
  will-change: transform;
  span {
    line-height: 1;
  }
  right: 5rem;
  bottom: 7rem;
  border: 2px solid ${props => props.typecolor}!important;
  ${_.media.desktop`
    border: 3px solid ${props => props.typecolor}!important;
    right: 10vw;
    bottom: -3rem;
    transform: rotate(1deg);
  `}
  ${_.media.hover`
    &:hover {
      transform: rotate(2deg);
      color: ${props => Color(props.typecolor).rotate(-90).saturate(0.25).hex()}!important;
    }
  `}
`

const ContentWrapper = styled('div', props)`
  ${_.absoluteTopFull};
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  .about-type {
    width: 110vw;
    padding: 0 2rem;
    padding-right: 4rem;
    transform: rotate(1deg);
    border-bottom: 2px solid ${props => props.typecolor}!important;
    ${_.media.desktop`
      border-bottom: 3px solid ${props => props.typecolor}!important;
      padding: 0 4rem;
    `}
  }
  .inner-type {
    width: 100%;
    max-width: 140rem;
    padding: 3rem 0;
    ${_.media.desktop`
      padding: 5vmin 0 3vmin;
      padding-right: 12vmin;
    `}
  }
  p {
    ${bigType};
    color: ${props => props.typecolor};
    padding-right: 12vmin;
    &:last-child {
      padding-bottom: 0;
    }
  }
`

const Socials = styled('ul', props)`
  ${_.flexRow};
  justify-content: flex-end;
  padding-right: 0;
  padding-left: 2rem;
  transform: rotate(1deg);
  border-bottom: 2px solid ${props => props.typecolor}!important;
  z-index: 200;
  ${_.media.desktop`
    transform: rotate(-1deg);
    background-color: ${props => props.bgcolor}!important;
    border-top: 3px solid ${props => props.typecolor}!important;
    border-left: 3px solid ${props => props.typecolor}!important;
    border-bottom: 0!important;
    right: -1rem;
    position: absolute;
    bottom: 0;
    padding-right: 1;
    padding-left: 0;
  `}
  li {
    position: relative;
  }
`

const Links = styled('ul', props)`
  ${_.flexColumn};
  padding: 3vmin 4vmin;
  transform: rotate(-1deg) translateX(-2vw) translateY(-1vh);
  width: 110vw;
  padding-left: 7vw;
  position: relative;
  border-bottom: 2px solid ${props => props.typecolor}!important;
  ${_.media.desktop`
    border-bottom: 3px solid ${props => props.typecolor}!important;
  `}
  li {
    padding-bottom: 1vmin;
    &:last-child {
      padding-bottom: 0;
    }
  }
  a.link {
    ${_.linkInit};
    ${bigType};
    color: ${props => props.typecolor};
    display: inline-block;
    padding-bottom: 0!important;
    ${_.media.hover`
      &:hover {
        color: ${props => Color(props.typecolor).rotate(-90).saturate(0.25).hex()};
      }
    `}
  }
  ${_.media.desktop`
    transform: rotate(-1deg) translateX(-2vw) translateY(-3vh);
  `}
`

export default {
  components: {
    ContentWrapper,
    FullWindow,
    CloseButton,
    Socials,
    FaceBook,
    Instagram,
    Soundcloud,
    Spotify,
    Twitter,
    Youtube,
    Links,
    ContactButton,
    PrivacyLink,
    Twitch
  },
  props: {
    Data: {
      type: Object,
      default: () => {},
      useDefaultForNull: true
    },
    Social: {
      type: Object,
      default: () => {},
      useDefaultForNull: true
    },
    Method: {
      type: Function,
      default: () => { console.log('clicked') },
      useDefaultForNull: true
    }
  },
  mounted () {
    this.typeColor = Color(this.color).rotate(-180).saturate(0.5).hex()
  },
  data () {
    return {
      color: randomArrItem(colors.popup),
      typeColor: 'transparent'
    }
  }
}
</script>
